import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import InvisibleTimer from './components/TokenTimer';
import { Box } from '@mui/material';
import './App.css';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import UserManagement from './components/UserManagement';
import AnalyticsPage from './components/AnalyticsPage';
import AgentsPage from './components/AgentsPage';
import SettingsPage from './components/SettingsPage';
import CallRecordings from './components/CallRecordings';
import PhoneNumbers from './components/PhoneNumbers';
import Leads from './components/Leads';
import Campaigns from './components/Campaigns';
import Referrals from './components/Referrals';
import RequireAuth from './components/RequireAuth';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import ProtectedAdminAgencyRoute from './components/ProtectedAdminAgencyRoute';
import ProtectedUserRoute from './components/ProtectedUserRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedAgentConfigRoute from './components/ProtectedAgentConfigRoute';
import ResetPassword from './components/ResetPassword';
import ReferralLanding from './components/ReferralLanding';
import Billing from './components/Billing';
import BillingSuccess from './components/BillingSuccess';
import BillingCancel from './components/BillingCancel';
import MockPayment from './components/MockPayment';
import GoogleCallback from './components/GoogleCallback';
import AgentConfig from './components/AgentConfig';
import AgentTypeSelection from './components/AgentTypeSelection';
import OutboundAgentCreate from './components/OutboundAgentCreate';
import CreateAgent from './components/CreateAgent';
import PlugAndPlay from './components/PlugAndPlay';
import PlugAndPlayEdit from './components/PlugAndPlayEdit';
import DublinTrunksManager from './components/DublinTrunksManager';
import RestaurantWorkflowDemo from './components/RestaurantWorkflowDemo';
import { SnackbarProvider } from 'notistack';
import Organizations from './components/Organizations';
import { AuthProvider, useAuth } from './context/AuthContext';
import Clients from './components/Clients';
import ClientDetails from './components/ClientDetails';
import { ThemeProvider } from './context/ThemeContext';

function App() {
  return (
    <ThemeProvider>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <AuthProvider>
          <Routes>
            {/* Public routes that don't require authentication */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/referral" element={<ReferralLanding />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Protected routes that require authentication */}
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <InvisibleTimer />
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Sidebar />
                    <Box
                      component="main"
                      sx={{
                        flexGrow: 1,
                        minHeight: '100vh',
                        overflow: 'auto',
                        backgroundColor: 'background.default'
                      }}
                    >
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="recordings" element={<CallRecordings />} />
                        <Route path="analytics" element={<AnalyticsPage />} />
                        <Route path="agents" element={<AgentsPage />} />
                        <Route path="phone-numbers" element={<PhoneNumbers />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="leads" element={<Leads />} />
                        <Route path="campaigns" element={<Campaigns />} />
                        <Route path="referrals" element={<Referrals />} />
                        <Route
                          path="user-management"
                          element={
                            <ProtectedAdminRoute>
                              <UserManagement />
                            </ProtectedAdminRoute>
                          }
                        />
                        <Route path="/billing" element={<RequireAuth><Billing /></RequireAuth>} />
                        <Route path="/billing/success" element={<RequireAuth><BillingSuccess /></RequireAuth>} />
                        <Route path="/billing/cancel" element={<RequireAuth><BillingCancel /></RequireAuth>} />
                        <Route path="/mock-payment" element={<RequireAuth><MockPayment /></RequireAuth>} />
                        <Route path="/settings/google-callback" element={<GoogleCallback />} />
                        <Route
                          path="/agents/new"
                          element={
                            <ProtectedUserRoute>
                              <AgentTypeSelection />
                            </ProtectedUserRoute>
                          }
                        />
                        <Route
                          path="/agents/new/inbound"
                          element={
                            <ProtectedUserRoute>
                              <CreateAgent />
                            </ProtectedUserRoute>
                          }
                        />
                        <Route
                          path="/agents/new/outbound"
                          element={
                            <ProtectedUserRoute>
                              <OutboundAgentCreate />
                            </ProtectedUserRoute>
                          }
                        />
                        <Route
                          path="/plug-and-play"
                          element={
                            <ProtectedUserRoute>
                              <PlugAndPlay />
                            </ProtectedUserRoute>
                          }
                        />
                        <Route
                          path="/plugnplay/edit/:agentId"
                          element={
                            <ProtectedUserRoute>
                              <PlugAndPlayEdit />
                            </ProtectedUserRoute>
                          }
                        />
                        <Route
                          path="/agents/edit/:id"
                          element={
                            <ProtectedAgentConfigRoute>
                              <AgentConfig />
                            </ProtectedAgentConfigRoute>
                          }
                        />
                        <Route path="/phone" element={
                          <ProtectedRoute>
                            <PhoneNumbers />
                          </ProtectedRoute>
                        } />
                        <Route path="/dublin-trunks" element={
                          <ProtectedRoute>
                            <DublinTrunksManager />
                          </ProtectedRoute>
                        } />
                        <Route path="/workflow-demo" element={
                          <ProtectedRoute>
                            <RestaurantWorkflowDemo />
                          </ProtectedRoute>
                        } />
                        <Route path="/organizations" element={
                          <ProtectedRoute>
                            <Organizations />
                          </ProtectedRoute>
                        } />
                        <Route path="/clients" element={<Clients />} />
                        <Route path="/client-details/:clientId" element={<ClientDetails />} />
                      </Routes>
                    </Box>
                  </Box>
                </RequireAuth>
              }
            />
          </Routes>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App; 